import { axiosInstance } from './axiosInstance';
import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import {
    RegisterResponse,
    RegisterPayload,
    ValidationError,
    LoginPayload,
    UserData,
    UserProfileAttributes,
    UserProfileDataResponse,
    UserProfileUpdateRequestBody,
    UserProfileUpdateResponse,
    UserProfile,
    SocialConnections,
    UserAttributes,
    SocialPage,
} from '../types/authTypes';
import { saveToStorage } from '../helpers/localStorage';
import { TOKEN_STORAGE_KEY } from '../constant/constant';

export const fetchTempToken = createAsyncThunk<string, void, { rejectValue: string }>(
    'auth/fetchTempToken',
    async (_, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post('/auth/temp/token');

            const { data } = response.data;
            return data.access_token;
        } catch (error) {
            console.log('error', error);
            if (axios.isAxiosError(error) && error.response) {
                console.log('error', error.response.data.message);
                return rejectWithValue(error.response.data.message || 'Failed to fetch token');
            }
            return rejectWithValue('An unexpected error occurred');
        }
    },
);

export const register = createAsyncThunk<
    RegisterResponse,
    RegisterPayload,
    { rejectValue: { [key: string]: string } }
>('auth/register', async (payload, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post<RegisterResponse>('/auth/register', payload);
        const type = response.data.data.type;
        const url = new URL(window.location.href);
        url.searchParams.set('type', type);
        window.history.pushState({}, '', url.toString());
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.data?.errors) {
            const validationErrors = error.response.data.errors as ValidationError[];
            const fieldErrors: { [key: string]: string } = {};

            validationErrors.forEach((err) => {
                const field = err.source.pointer.split('/').pop() || '';
                if (field) {
                    fieldErrors[field] = err.detail;
                }
            });
            console.log('fieldErrors', fieldErrors);
            return rejectWithValue(fieldErrors);
        }
        return rejectWithValue({ general: 'An unexpected error occurred during registration' });
    }
});

export const login = createAsyncThunk<
    RegisterResponse,
    LoginPayload,
    { rejectValue: { [key: string]: string } }
>('auth/login', async (payload, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post<RegisterResponse>('/auth/login', payload);
        const type = response.data.data.type;
        const url = new URL(window.location.href);
        url.searchParams.set('type', type);
        window.history.pushState({}, '', url.toString());
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.data?.errors) {
            const validationErrors = error.response.data.errors as ValidationError[];
            const fieldErrors: Record<string, string> = {};
            validationErrors.forEach((e) => {
                const pointer = e.source?.pointer ?? '';
                if (pointer.includes('email')) {
                    fieldErrors.email = e.detail || e.title || 'Invalid email';
                }
            });
            if (Object.keys(fieldErrors).length > 0) {
                return rejectWithValue(fieldErrors);
            }
            return rejectWithValue({ error: 'Something went wrong. Please try again later.' });
        }
        return rejectWithValue({ error: 'Something went wrong. Please try again later.' });
    }
});

export const logout = createAsyncThunk('auth/logout', async () => {
    localStorage.removeItem('authToken');
});

export const fetchCurrentUser = createAsyncThunk<
    UserData,
    void,
    { rejectValue: { [key: string]: string } }
>('auth/fetchCurrentUser', async (_, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get<{
            data: UserData;
            meta: { social_connections: SocialConnections };
        }>('/users/me');
        const userData = response.data.data;
        const socialMedia = response.data.meta.social_connections;

        const user = {
            ...userData,
            meta: {
                social_connections: socialMedia,
            },
        };
        console.log('USER', user);
        return user;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.data?.errors) {
            const validationErrors = error.response.data.errors as ValidationError[];
            const fieldErrors: { [key: string]: string } = {};
            validationErrors.forEach((err) => {
                const field = err.source.pointer.split('/').pop() || '';
                if (field) {
                    fieldErrors[field] = err.detail;
                }
            });
            return rejectWithValue(fieldErrors);
        }
        return rejectWithValue({ general: 'Failed to fetch user data' });
    }
});
export const fetchUserProfile = createAsyncThunk<
    UserProfileAttributes,
    string,
    { rejectValue: { [key: string]: string } }
>('auth/fetchUserProfile', async (userId, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get<{ data: UserProfileDataResponse }>(
            `/users/${userId}/profile`,
        );
        console.log('response', response.data.data);

        return response.data.data.attributes;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.data?.errors) {
            const validationErrors = error.response.data.errors as ValidationError[];
            const fieldErrors: { [key: string]: string } = {};
            validationErrors.forEach((err) => {
                const field = err.source.pointer.split('/').pop() || '';
                if (field) {
                    fieldErrors[field] = err.detail;
                }
            });
            return rejectWithValue(fieldErrors);
        }
        return rejectWithValue({ general: 'Failed to fetch user data' });
    }
});

export const updateUserProfile = createAsyncThunk<
    UserProfileAttributes,
    UserProfile,
    { rejectValue: { [key: string]: string } }
>('auth/updateUserProfile', async (payload, { rejectWithValue }) => {
    const { id, content_for, primary_activity, brand_name, website, brand_voice } = payload;
    const attributes: any = {
        content_for,
        primary_activity,
        brand_name,
        brand_voice,
    };

    if (website) {
        attributes.website = website;
    }

    const requestBody: UserProfileUpdateRequestBody = {
        data: {
            type: 'user_profiles',
            attributes,
        },
    };
    try {
        const response = await axiosInstance.patch<UserProfileUpdateResponse>(
            `/users/${id}/profile`,
            requestBody,
        );
        return response.data.data.attributes;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.data?.errors) {
            const validationErrors = error.response.data.errors as ValidationError[];
            const fieldErrors: { [key: string]: string } = {};
            validationErrors.forEach((err) => {
                const field = err.source.pointer.split('/').pop() || '';
                if (field) {
                    fieldErrors[field] = err.detail;
                }
            });
            return rejectWithValue(fieldErrors);
        }
        return rejectWithValue({ general: 'Failed to update user profile' });
    }
});

export const forgotPassword = createAsyncThunk<
    string, // message
    string, // email
    { rejectValue: { [key: string]: string } }
>('auth/forgotPassword', async (email, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post('/auth/forgot-password', {
            data: {
                type: 'password-reset',
                attributes: { email },
            },
        });

        return response.data.data.attributes.message;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.data?.errors) {
            const validationErrors = error.response.data.errors as ValidationError[];
            const fieldErrors: { [key: string]: string } = {};
            validationErrors.forEach((err) => {
                const field = err.source.pointer.split('/').pop() || '';
                if (field) {
                    fieldErrors[field] = err.detail;
                }
            });
            return rejectWithValue(fieldErrors);
        }
        return rejectWithValue({ general: 'Failed to send password reset email' });
    }
});

export const resetPassword = createAsyncThunk<
    string, // message
    { email: string; token: string; password: string; password_confirmation: string },
    { rejectValue: { [key: string]: string } }
>('auth/resetPassword', async (payload, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post('/auth/reset-password', {
            data: {
                type: 'password-reset',
                attributes: {
                    email: payload.email,
                    token: payload.token,
                    password: payload.password,
                    password_confirmation: payload.password_confirmation,
                },
            },
        });
        return response.data.data.attributes.message;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.data?.errors) {
            const validationErrors = error.response.data.errors as ValidationError[];
            const fieldErrors: { [key: string]: string } = {};
            validationErrors.forEach((err) => {
                const field = err.source.pointer.split('/').pop() || '';
                if (field) {
                    fieldErrors[field] = err.detail;
                }
            });
            return rejectWithValue(fieldErrors);
        }
        return rejectWithValue({ general: 'Failed to reset password' });
    }
});

export const saveGoogleToken = createAsyncThunk<
    string,
    { token: string },
    { rejectValue: { [key: string]: string } } // error structure
>('auth/saveGoogleToken', async (payload, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post('/auth/gdrive-token', {
            data: {
                type: 'gdrive-token',
                attributes: {
                    token: payload.token,
                },
            },
        });

        return response.data.data.attributes.message;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.data?.errors) {
            const validationErrors = error.response.data.errors as ValidationError[];
            const fieldErrors: { [key: string]: string } = {};
            validationErrors.forEach((err) => {
                const field = err.source.pointer.split('/').pop() || '';
                if (field) {
                    fieldErrors[field] = err.detail;
                }
            });
            return rejectWithValue(fieldErrors);
        }
        return rejectWithValue({ general: 'Failed to save Google token' });
    }
});

export const deauthorizeProvider = createAsyncThunk<
    string,
    string,
    { rejectValue: { [key: string]: string } }
>('auth/deauthorizeProvider', async (provider, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.delete(`/auth/deauthorize/${provider}`);
        return response.data.data?.attributes?.message || 'Successfully deauthorized';
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.data?.errors) {
            const validationErrors = error.response.data.errors as ValidationError[];
            const fieldErrors: { [key: string]: string } = {};
            validationErrors.forEach((err) => {
                const field = err.source.pointer.split('/').pop() || '';
                if (field) {
                    fieldErrors[field] = err.detail;
                }
            });
            return rejectWithValue(fieldErrors);
        }
        return rejectWithValue({ general: `Failed to deauthorize ${provider}` });
    }
});

export const deleteAccount = createAsyncThunk<
    string,
    void,
    { rejectValue: { [key: string]: string } }
>('auth/deleteAccount', async (_, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.delete(`/users/me`);
        return response.data.data?.attributes?.message || 'Your account has been deleted';
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.data?.errors) {
            const validationErrors = error.response.data.errors as ValidationError[];
            const fieldErrors: { [key: string]: string } = {};
            validationErrors.forEach((err) => {
                const field = err.source?.pointer?.split('/').pop() || '';
                if (field) {
                    fieldErrors[field] = err.detail || err.title || 'Invalid request';
                }
            });
            return rejectWithValue(fieldErrors);
        }
        return rejectWithValue({ error: 'Failed to delete account' });
    }
});

export const updateUserBio = createAsyncThunk<
    UserAttributes,
    { id: string; attributes: Partial<UserAttributes> },
    { rejectValue: { [key: string]: string } }
>('auth/updateUserBio', async (payload, { rejectWithValue }) => {
    const { id, attributes } = payload;

    const requestBody = {
        data: {
            type: 'users',
            id,
            attributes,
        },
    };

    try {
        const response = await axiosInstance.patch<{
            data: { type: 'users'; id: string; attributes: UserAttributes };
        }>(`/users/${id}`, requestBody);

        return response.data.data.attributes;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.data?.errors) {
            const validationErrors = error.response.data.errors as ValidationError[];
            const fieldErrors: { [key: string]: string } = {};
            validationErrors.forEach((err) => {
                const field = err.source.pointer.split('/').pop() || '';
                if (field) {
                    fieldErrors[field] = err.detail;
                }
            });
            return rejectWithValue(fieldErrors);
        }
        return rejectWithValue({ general: 'Failed to update user bio' });
    }
});

export const requestEmailChange = createAsyncThunk<
    { message: string },
    { id: string; email: string },
    { rejectValue: { [key: string]: string } }
>('auth/requestEmailChange', async (payload, { rejectWithValue }) => {
    const { id, email } = payload;

    const requestBody = {
        data: {
            type: 'users',
            id,
            attributes: { email },
        },
    };

    try {
        const response = await axiosInstance.patch<{
            data: { type: string; attributes: { message: string } };
        }>(`/users/${id}/email`, requestBody);

        return response.data.data.attributes;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.data?.errors) {
            const validationErrors = error.response.data.errors as ValidationError[];
            const fieldErrors: { [key: string]: string } = {};
            validationErrors.forEach((err) => {
                const field = err.source.pointer.split('/').pop() || '';
                if (field) {
                    fieldErrors[field] = err.detail;
                }
            });
            return rejectWithValue(fieldErrors);
        }
        return rejectWithValue({ general: 'Failed to request email change' });
    }
});

export const confirmEmailChange = createAsyncThunk<
    UserAttributes,
    { id: string; email: string; code: string },
    { rejectValue: { [key: string]: string } }
>('auth/confirmEmailChange', async (payload, { rejectWithValue }) => {
    const { id, email, code } = payload;

    const requestBody = {
        data: {
            type: 'users',
            id,
            attributes: { email, code },
        },
    };

    try {
        const response = await axiosInstance.patch<{
            data: { type: 'users'; id: string; attributes: UserAttributes };
        }>(`/users/${id}/confirm-email`, requestBody);

        return response.data.data.attributes;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.data?.errors) {
            const validationErrors = error.response.data.errors as ValidationError[];
            const fieldErrors: { [key: string]: string } = {};
            validationErrors.forEach((err) => {
                const field = err.source?.pointer?.split('/').pop() || 'code';
                fieldErrors[field] = err.detail || err.title;
            });
            return rejectWithValue(fieldErrors);
        }
        return rejectWithValue({ general: 'Failed to confirm email change' });
    }
});

export const uploadProfilePhoto = createAsyncThunk<
    UserAttributes,
    { id: string; file: File | Blob },
    { rejectValue: { [key: string]: string } }
>('auth/uploadProfilePhoto', async (payload, { rejectWithValue }) => {
    const { id, file } = payload;

    const fd = new FormData();
    fd.append('avatar', file, (file as File).name || 'avatar.jpg');

    try {
        const res = await axiosInstance.post<{
            data: { type: 'users'; id: string; attributes: UserAttributes };
            meta?: Record<string, unknown>;
        }>(`/users/${id}/profile-photo`, fd, {});

        return res.data.data.attributes;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.data?.errors) {
            const validationErrors = error.response.data.errors as ValidationError[];
            const fieldErrors: { [key: string]: string } = {};
            validationErrors.forEach((err) => {
                const pointer = err.source?.pointer ?? '';
                const field = pointer.split('/').filter(Boolean).pop() || 'avatar';
                fieldErrors[field] = err.detail || err.title || 'Upload failed';
            });
            return rejectWithValue(fieldErrors);
        }
        return rejectWithValue({ general: 'Failed to upload profile photo' });
    }
});

export const removeProfilePhoto = createAsyncThunk<
    UserAttributes,
    { id: string },
    { rejectValue: { [key: string]: string } }
>('auth/removeProfilePhoto', async ({ id }, { rejectWithValue }) => {
    try {
        const res = await axiosInstance.delete<{
            data: { type: 'users'; id: string; attributes: UserAttributes };
        }>(`/users/${id}/profile-photo`);

        return res.data.data.attributes;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.data?.errors) {
            const validationErrors = error.response.data.errors as ValidationError[];
            const fieldErrors: { [key: string]: string } = {};
            validationErrors.forEach((err) => {
                const pointer = err.source?.pointer ?? '';
                const field = pointer.split('/').filter(Boolean).pop() || 'general';
                fieldErrors[field] = err.detail || err.title || 'Delete failed';
            });
            return rejectWithValue(fieldErrors);
        }
        return rejectWithValue({ general: 'Failed to remove profile photo' });
    }
});

export const updateProjectSocialPage = createAsyncThunk<
    void,
    { projectId: string; socialPage: SocialPage },
    { rejectValue: { [key: string]: string } }
>('auth/updateProjectSocialPage', async ({ projectId, socialPage }, { rejectWithValue }) => {
    try {
        const requestBody = {
            data: {
                type: 'projects',
                id: projectId,
                relationships: {
                    social_page: socialPage,
                },
            },
        };

        await axiosInstance.patch(`/projects/${projectId}`, requestBody);
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.data?.errors) {
            const validationErrors = error.response.data.errors as ValidationError[];
            const fieldErrors: { [key: string]: string } = {};
            validationErrors.forEach((err) => {
                const field = err.source?.pointer?.split('/').pop() || 'general';
                fieldErrors[field] = err.detail || err.title || 'Update failed';
            });
            return rejectWithValue(fieldErrors);
        }
        return rejectWithValue({ general: 'Failed to update project social page' });
    }
});
